import { Box, Stack, Typography, Button } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { CheckIcon, AlertCircleIcon } from "../../shared/icons";
import { useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";
import { trackEvent } from "../../shared/Snowplow";
import { createInformationTypeUpsellContactRequestMutationOptions } from "../../api/informationTypes";
import { HACK_alignIconWithLabelBox } from "../../ui/HACK_alignIconWithLabel";
import { NewChip } from "./NewChip";
import screenshotEn from "./img/information-type-preview.en.webp";
import screenshotNo from "./img/information-type-preview.no.webp";
import screenshotSv from "./img/information-type-preview.sv.webp";

const screenshots = { en: screenshotEn, no: screenshotNo, sv: screenshotSv };

export const InformationTypesUpsell = () => {
  usePrivacyDocumentTitle("InformationType.Sidebar.InformationTypes");
  const { t } = useTranslation();
  const currentOrganisation = useCurrentOrganisation();
  const contactRequestMutation = useMutation(createInformationTypeUpsellContactRequestMutationOptions());

  return (
    <Box
      sx={{
        height: { md: "100dvh" },
        minHeight: { xs: "100%", md: 0 },
        overflow: { md: "clip" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 4, lg: 8 },
        py: { xs: 6, md: 0 },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        sx={{
          gap: "1.5vw",
          alignItems: "center",
          width: { xs: "100%", md: "max-content" },
          maxWidth: "100%",
          minWidth: 0,
          overflow: { md: "clip" },
          overflowClipMargin: { md: "3rem" },
        }}
      >
        <Stack
          sx={{
            flex: { xs: "1 1 auto", md: "0 0 520px" },
            gap: 3,
            alignItems: "flex-start",
            transform: { md: "translateY(-5vh)" },
          }}
        >
          <NewChip />
          <Typography
            variant="h1"
            sx={{
              maxWidth: 460,
              textWrap: "balance",
              fontSize: theme => theme.typography.pxToRem(30),
              lineHeight: theme => theme.typography.pxToRem(34),
            }}
          >
            {t("InformationType.Upsell.Headline")}
          </Typography>
          <Typography
            sx={{
              fontSize: theme => theme.typography.pxToRem(14),
              lineHeight: 1.6,
              textWrap: "balance",
              color: theme => theme.palette.text.secondary,
            }}
          >
            {t("InformationType.Upsell.Subhead")}
          </Typography>
          <Stack sx={{ gap: 1.5, mt: 1 }}>
            <Bullet>{t("InformationType.Upsell.Bullet1")}</Bullet>
            <Bullet>{t("InformationType.Upsell.Bullet2")}</Bullet>
            <Bullet>{t("InformationType.Upsell.Bullet3")}</Bullet>
          </Stack>
          {contactRequestMutation.isSuccess ? (
            <Stack
              direction="row"
              sx={{
                "mt": 2,
                "gap": 0.75,
                "alignItems": "center",
                "height": 36.5,
                "animation": "upsellConfirmationEnter 300ms linear both",
                "@keyframes upsellConfirmationEnter": { from: { opacity: 0 } },
              }}
            >
              <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), fontWeight: 600 }}>
                {t("InformationType.Upsell.ConfirmationLead")}
              </Typography>
              <Typography
                sx={{
                  fontSize: theme => theme.typography.pxToRem(14),
                  fontWeight: 400,
                  color: theme => theme.palette.text.tertiary,
                }}
              >
                {t("InformationType.Upsell.ConfirmationBody")}
              </Typography>
            </Stack>
          ) : (
            <Stack sx={{ mt: 2, gap: 2, alignItems: "flex-start" }}>
              {contactRequestMutation.isError && (
                <Stack
                  direction="row"
                  sx={{
                    "gap": 1,
                    "alignItems": "center",
                    "animation": "upsellConfirmationEnter 300ms linear both",
                    "@keyframes upsellConfirmationEnter": { from: { opacity: 0 } },
                  }}
                >
                  <AlertCircleIcon size={16} color={theme => theme.palette.error.main} />
                  <HACK_alignIconWithLabelBox>
                    <Typography
                      sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.error.main }}
                    >
                      {t("InformationType.Upsell.Error")}
                    </Typography>
                  </HACK_alignIconWithLabelBox>
                </Stack>
              )}
              <Button
                variant="contained"
                color="primary"
                loading={contactRequestMutation.isPending}
                onClick={() => {
                  trackEvent.clickPrimaryAssetsUpsellRequestDemo();
                  contactRequestMutation.mutate();
                }}
              >
                {t("InformationType.Upsell.Cta")}
              </Button>
            </Stack>
          )}
        </Stack>
        <Box
          sx={{
            flexShrink: 0,
            width: "clamp(755px, 60%, 1200px)",
            borderRadius: 1,
            overflow: "hidden",
            border: 1,
            borderColor: theme => theme.palette.grey["300"],
            bgcolor: theme => theme.palette.grey["50"],
            boxShadow: 1,
          }}
        >
          <Box
            component="img"
            src={screenshots[currentOrganisation.lang]}
            alt={t("InformationType.Upsell.ScreenshotAlt")}
            sx={{ display: "block", width: "100%", aspectRatio: "1267 / 1017" }}
          />
        </Box>
      </Stack>
    </Box>
  );
};

const Bullet = ({ children }: { children: string }) => (
  <Stack direction="row" sx={{ gap: 1.5, alignItems: "flex-start" }}>
    <CheckIcon size={18} color={theme => theme.palette.primary["600"]} />
    <Typography
      sx={{
        fontSize: theme => theme.typography.pxToRem(12),
        lineHeight: 1.6,
        fontWeight: 600,
        textWrap: "pretty",
      }}
    >
      {children}
    </Typography>
  </Stack>
);
